// Routes of the user-facing app.

import { type FunctionComponent, useState } from 'react';
import { Navigate, Route, useLocation } from 'react-router';

import SentryRoutes from 'components/SentryRoutes';
import { BetaTestersOnly, PlaybackProvider } from 'components/ai-app';

import Error404Page from 'pages/Error404';

import {
  Onboarding,
  PasswordReset,
  SessionPage,
  WellnessCheckIn,
  WellnessCheckOut,
} from 'pages/ai-app';

import HomePage from 'pages/ai-app/Home';
import LandingPage from 'pages/ai-app/Landing';
import LoginPage from 'pages/ai-app/Login';
import PastSessions from 'pages/ai-app/PastSessions';
import RegisterPage from 'pages/ai-app/Register';
import StatsPage from 'pages/ai-app/Stats';

import {
  E_EXPERIENCE,
  E_ONBOARDING,
  E_PASSWORD_RESET,
  E_SESSION,
  E_STATS,
} from 'utils';

import { useGlobalState } from 'utils/global-state';

const AppRoutes: FunctionComponent = () => {
  const [user] = useGlobalState('user');

  const { pathname } = useLocation();

  const [prevUser, setPrevUser] = useState(user);
  const [loginPathname, setLoginPathname] = useState<null | string>(null);
  if (user !== prevUser) {
    setPrevUser(user);
    setLoginPathname(!prevUser && user ? pathname : null);
  }
  const justLoggedIn = !!user && loginPathname === pathname;

  return user ? (
    // PlaybackProvider sits ABOVE the routes so its shared <video> survives the
    // check-in → session navigation.
    <PlaybackProvider>
      <BetaTestersOnly>
        <SentryRoutes>
          <Route element={<HomePage />} index />
          <Route
            element={<WellnessCheckIn />}
            path="check-in"
          />
          <Route
            element={<WellnessCheckIn initAsEditor />}
            path={E_EXPERIENCE}
          />
          <Route element={<PastSessions />} path="past-sessions" />
          <Route element={<WellnessCheckOut />} path="post-session-evaluation" />
          <Route element={<SessionPage />} path={E_SESSION} />
          <Route element={<StatsPage />} path={E_STATS} />
          <Route element={<Onboarding />} path={E_ONBOARDING} />
          <Route element={justLoggedIn ? <Navigate replace to="/" /> : <Error404Page />} path="*" />
        </SentryRoutes>
      </BetaTestersOnly>
    </PlaybackProvider>
  ) : (
    <SentryRoutes>
      <Route element={<LandingPage />} index />
      <Route element={<RegisterPage />} path="register" />
      <Route element={<PasswordReset />} path={E_PASSWORD_RESET} />
      <Route element={<LoginPage />} path="*" />
    </SentryRoutes>
  );
};

export default AppRoutes;
